import {
  Badge,
  Button,
  Flex,
  HStack,
  Heading,
  InfoBox,
  Stack,
  Tabs,
  Text,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdArrowBack } from "react-icons/md";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CustomerCompanyDetail,
  customerCompanyService,
} from "services/customerCompanyService";
import IntegrationsTab from "./components/IntegrationsTab";
import KnowledgeBaseTab from "./components/KnowledgeBaseTab";
import MembersTab from "./components/MembersTab";
import OverviewTab from "./components/OverviewTab";
import PromptShortcutsTab from "./components/PromptShortcutsTab";
import SkillsTab from "./components/SkillsTab";
import UsageTab from "./components/UsageTab";
import WorkflowsTab from "./components/WorkflowsTab";

/**
 * CRM company profile. Gives StrikeUp staff the same control over a CustomerCompany's own
 * resources — knowledge base, skills, workflows, prompt shortcuts, integrations and members —
 * that a Customer Admin has inside the widget's customer portal.
 *
 * The page is a shell: it owns the company record, the header and the tab strip, and each tab
 * loads its own data. Tabs are lazily mounted, so opening the page issues one request rather
 * than seven.
 */

const TABS = [
  "overview",
  "members",
  "knowledge-base",
  "skills",
  "workflows",
  "prompts",
  "integrations",
  "usage",
] as const;

type TabValue = (typeof TABS)[number];

const DEFAULT_TAB: TabValue = "overview";

const CustomerCompanyDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [detail, setDetail] = useState<CustomerCompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Deep-linkable tab. An unrecognised ?tab= value falls back to Overview rather than rendering
  // nothing, so a stale bookmark still shows the page.
  const requestedTab = searchParams.get("tab");
  const activeTab: TabValue = TABS.includes(requestedTab as TabValue)
    ? (requestedTab as TabValue)
    : DEFAULT_TAB;

  const handleTabChange = (value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true },
    );

  const load = useCallback(
    async (companyId: string) => {
      try {
        setLoading(true);
        const data = await customerCompanyService.getById(companyId);
        setDetail(data);
        setError("");
      } catch (err: any) {
        setError(
          err.response?.data?.error ||
            t("app.customerCompanies.loadFailed", {
              defaultValue: "Failed to load company.",
            }),
        );
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (id) void load(id);
  }, [id, load]);

  if (loading) {
    return (
      <Stack gap={4}>
        <Heading>
          {t("app.customerCompanies.details.loading", {
            defaultValue: "Loading...",
          })}
        </Heading>
      </Stack>
    );
  }

  if (!detail || !id) {
    return (
      <Stack gap={4}>
        <InfoBox variant="error">
          <Text color="red.700">
            {error ||
              t("app.customerCompanies.notFound", {
                defaultValue: "Customer company not found.",
              })}
          </Text>
        </InfoBox>
      </Stack>
    );
  }

  const tabLabels: Record<TabValue, string> = {
    overview: t("app.customerCompanies.tabs.overview", {
      defaultValue: "Overview",
    }),
    members: t("app.customerCompanies.tabs.members", {
      defaultValue: "Members",
    }),
    "knowledge-base": t("app.customerCompanies.tabs.knowledgeBase", {
      defaultValue: "Knowledge base",
    }),
    skills: t("app.customerCompanies.tabs.skills", { defaultValue: "Skills" }),
    workflows: t("app.customerCompanies.tabs.workflows", {
      defaultValue: "Workflows",
    }),
    prompts: t("app.customerCompanies.tabs.prompts", {
      defaultValue: "Prompt shortcuts",
    }),
    integrations: t("app.customerCompanies.tabs.integrations", {
      defaultValue: "Integrations",
    }),
    usage: t("app.customerCompanies.tabs.usage", { defaultValue: "Usage" }),
  };

  return (
    <Stack gap={4} w="full" maxW="full">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <HStack>
          <Button
            intent="ghost"
            onClick={() => navigate("/admin/customer-companies")}
          >
            <MdArrowBack /> {t("app.common.back", { defaultValue: "Back" })}
          </Button>
          <Stack gap={0}>
            <Heading>{detail.name}</Heading>
            <HStack gap={2}>
              <Badge
                colorPalette={detail.isActive ? "green" : "gray"}
                variant="subtle"
              >
                {detail.isActive
                  ? t("app.common.active", { defaultValue: "Active" })
                  : t("app.common.inactive", { defaultValue: "Inactive" })}
              </Badge>
              {detail.externalRef && (
                <Text
                  fontSize="sm"
                  color="var(--text-secondary-color)"
                  fontFamily="mono"
                >
                  {detail.externalRef}
                </Text>
              )}
            </HStack>
          </Stack>
        </HStack>
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      {/* Staff keep full write access to a deactivated company — deactivation locks out the
          company's own users, not the tenant that owns the account. */}
      {!detail.isActive && (
        <InfoBox variant="warning">
          <Stack gap={1}>
            <Text fontWeight="medium">
              {t("app.customerCompanies.deactivatedBanner.title", {
                defaultValue: "This company is deactivated",
              })}
            </Text>
            <Text fontSize="sm">
              {t("app.customerCompanies.deactivatedBanner.body", {
                defaultValue:
                  "Its customers are signed out and cannot sign in, and its scheduled workflows are skipped. You can still make changes here.",
              })}
            </Text>
          </Stack>
        </InfoBox>
      )}

      <Tabs.Root
        value={activeTab}
        onValueChange={(e: { value: string }) => handleTabChange(e.value)}
        lazyMount
        unmountOnExit
      >
        <Tabs.List>
          {TABS.map((tab) => (
            <Tabs.Trigger key={tab} value={tab}>
              {tabLabels[tab]}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="overview" mt={4}>
          <OverviewTab detail={detail} onDetailChange={setDetail} />
        </Tabs.Content>

        <Tabs.Content value="members" mt={4}>
          <MembersTab customerCompanyId={id} />
        </Tabs.Content>

        <Tabs.Content value="knowledge-base" mt={4}>
          <KnowledgeBaseTab customerCompanyId={id} />
        </Tabs.Content>

        <Tabs.Content value="skills" mt={4}>
          <SkillsTab customerCompanyId={id} />
        </Tabs.Content>

        <Tabs.Content value="workflows" mt={4}>
          <WorkflowsTab customerCompanyId={id} />
        </Tabs.Content>

        <Tabs.Content value="prompts" mt={4}>
          <PromptShortcutsTab customerCompanyId={id} />
        </Tabs.Content>

        <Tabs.Content value="integrations" mt={4}>
          <IntegrationsTab customerCompanyId={id} />
        </Tabs.Content>

        <Tabs.Content value="usage" mt={4}>
          <UsageTab customerCompanyId={id} />
        </Tabs.Content>
      </Tabs.Root>
    </Stack>
  );
};

export default CustomerCompanyDetails;
